// controllers/fetch_input_controller.js
//
// Watches an input for changes, fetches an HTML partial from the server
// with the current value as a query parameter, and injects the response
// into an output target element.
//
// Usage:
//   data-controller="fetch-input"
//   data-fetch-input-url-value="/path/to/endpoint"   # required
//   data-fetch-input-param-value="party_size"        # query param name (default: "value")
//   data-fetch-input-debounce-value="300"            # debounce ms       (default: 300)
//
//   data-fetch-input-target="input"   # the input element to watch
//   data-fetch-input-target="output"  # where the HTML response is injected
//
// The input element should fire a native "input" event when it changes.
// Trigger this manually for custom controls:
//   element.dispatchEvent(new Event('input'))
//
import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'output']
  static values = {
    url: String,
    param: { type: String, default: 'value' },
    debounce: { type: Number, default: 300 }
  }

  connect() {
    this._timer = null
  }

  disconnect() {
    clearTimeout(this._timer)
  }

  fetch() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this._doFetch(), this.debounceValue)
  }

  _doFetch() {
    const value = this.inputTarget.value
    if (!value) return

    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set(this.paramValue, value)

    window
      .fetch(url, {
        headers: { Accept: 'text/html', 'X-Requested-With': 'XMLHttpRequest' },
        credentials: 'same-origin'
      })
      .then((r) => (r.ok ? r.text() : null))
      .then((html) => {
        if (html !== null) this.outputTarget.innerHTML = html
      })
      .catch(() => {})
  }
}
